import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { VehicleCard } from "../components/VehicleCard";
import { VehicleDetailModal } from "../components/fleet/VehicleDetailModal";
import { HandoverPicker } from "../components/booking/HandoverPicker";
import { TimeSlotSelect } from "../components/booking/TimeSlotSelect";
import { AgeGateModal } from "../components/booking/AgeGateModal";
import { handoverFromParams } from "./Results";
import api, { apiError, setDraft } from "../lib/api";
import { Loader2, Check, X as XIcon } from "lucide-react";
import dayjs from "dayjs";

const TERMS = [{ m: 1 }, { m: 3, popular: true }, { m: 6, save: 10 }, { m: 9, save: 15 }];
const INCLUDED = ["Maintenance and servicing", "Registration and insurance (CDW)", "24/7 roadside assistance", "Third-party cover over 1 million USD", "Free replacement on breakdown", "Renew or extend from your account"];
const EXCLUDED = ["Fuel", "Tolls (Salik and Darb, billed + 25% admin fee)", "Traffic fines (+10% admin fee + VAT)", "Excess mileage (AED 0.50 to 0.75/km + VAT)"];

const slotToTime = (v) => `${String(Math.floor(v)).padStart(2, "0")}:${v % 1 ? "30" : "00"}`;

export default function Monthly() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [branches, setBranches] = useState(null);
  const [handover, setHandover] = useState(() => handoverFromParams(params));
  const [startDate, setStartDate] = useState(() => (params.get("pickup") || dayjs().add(1, "day").format("YYYY-MM-DD")).slice(0, 10));
  const [startTime, setStartTime] = useState(null);
  const [months, setMonths] = useState(1);
  const [modelYear, setModelYear] = useState(2025);
  const [mileage, setMileage] = useState(3000);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ageOk, setAgeOk] = useState(params.get("age_ok") === "1");
  const [ageChecked, setAgeChecked] = useState(false);
  const [pendingVehicle, setPendingVehicle] = useState(null);
  const [detail, setDetail] = useState(null);
  const [galleries, setGalleries] = useState({});

  useEffect(() => {
    api.get("/fleet/galleries").then((r) => setGalleries(r.data)).catch(() => {});
  }, []);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (startTime == null) return;
    const body = {
      mode: "monthly",
      pickup_datetime: `${startDate}T${slotToTime(startTime)}`,
      months, model_year: modelYear, mileage_package: mileage,
      driver_age: 25,
    };
    if (handover.type === "delivery") body.delivery_emirate = handover.emirate;
    else body.pickup_station = handover.branchId;
    let ignore = false;
    setError(null);
    api.post("/vehicles/search", body)
      .then((r) => { if (!ignore) setData(r.data); })
      .catch((e) => { if (!ignore) { setError(apiError(e)); setData(null); } });
    return () => { ignore = true; };
  }, [startDate, startTime, months, modelYear, mileage, handover]);

  const cheapest = useMemo(
    () => (data && data.vehicles.length ? Math.min(...data.vehicles.map((v) => v.pricing.monthly_with_vat)) : null),
    [data]);

  const lockIn = (vehicle) => {
    setDraft({ vehicle, search: data.search, handover, flight: null });
    navigate("/booking");
  };
  const onSelect = (vehicle) => {
    if (!ageOk) { setPendingVehicle(vehicle); return; }
    lockIn(vehicle);
  };

  const Tile = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick}
      className={`rounded-lg border-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors relative ${active ? "border-[#009900] bg-[#f0f9f0] text-[#144733]" : "border-[#d5ddd5] bg-white text-[#57685e] hover:border-[#9db8a5]"}`}
      data-testid={testId}>{children}</button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <div className="promo-stripe">
            <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="monthly-heading">{t("Monthly Rental")}</h1>
            <p className="text-[#FFDC00] font-bold mt-2">
              {cheapest ? t("Starting from AED {{amt}}/month incl. VAT", { amt: Math.round(cheapest).toLocaleString() }) : t("Best rates for 22+ days")}
            </p>
            <p className="text-white/80 text-sm mt-2" data-testid="monthly-savings-anchor">
              {t("Around 35% less per day than daily rates, with maintenance, registration, insurance and 24/7 roadside all included.")}
            </p>
          </div>
          <div className="mt-4 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-xs text-white/90 max-w-2xl" data-testid="eligibility-banner">
            {t("Minimum first booking is 22 days. A rental of 22 to 30 days counts as one rental month. For shorter trips, our Short-Term Rental engine has you covered.")}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full flex-1 grid lg:grid-cols-[1fr_300px] gap-8">
        <div>
          {/* Search controls */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 mb-6 space-y-4" data-testid="monthly-controls">
            {branches && <HandoverPicker branches={branches} value={handover} onChange={setHandover} mode="monthly" />}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">{t("Start date *")}</label>
                <input type="date" className="field-input" min={dayjs().format("YYYY-MM-DD")} value={startDate}
                  onChange={(e) => setStartDate(e.target.value)} data-testid="monthly-start-date" />
              </div>
              <div>
                <label className="field-label">{t("Time *")}</label>
                <TimeSlotSelect date={startDate} mode={handover.type === "branch" ? "branch" : "delivery"}
                  stationId={handover.branchId} emirate={handover.emirate}
                  value={startTime} onChange={setStartTime}
                  onNoneAvailable={() => setStartDate((d) => (d <= dayjs().format("YYYY-MM-DD") ? dayjs(d).add(1, "day").format("YYYY-MM-DD") : d))}
                  testId="monthly-start-time" />
              </div>
            </div>

            {/* Configurator */}
            <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-dashed border-[#e2ebe4]">
              <div>
                <label className="field-label">{t("Term")}</label>
                <div className="flex gap-2 flex-wrap">
                  {TERMS.map((term) => (
                    <Tile key={term.m} active={months === term.m} onClick={() => setMonths(term.m)} testId={`term-${term.m}`}>
                      {t("{{n}} mo", { n: term.m })}
                      {term.save && <span className="absolute -top-2 -right-2 bg-[#FFDC00] text-[#0F1E17] text-[9px] rounded-full px-1.5 py-0.5 normal-case">−{term.save}%</span>}
                      {term.popular && <span className="absolute -top-2 -right-2 bg-[#009900] text-white text-[9px] rounded-full px-1.5 py-0.5 normal-case" data-testid="term-popular-badge">{t("Popular")}</span>}
                    </Tile>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">{t("Model year")}</label>
                <div className="flex gap-2">
                  {[2025, 2026].map((y) => (
                    <Tile key={y} active={modelYear === y} onClick={() => setModelYear(y)} testId={`year-${y}`}>
                      {y}
                      {y === 2026 && <span className="absolute -top-2 -right-2 bg-[#144733] text-white text-[9px] rounded-full px-1.5 py-0.5 normal-case">+10%</span>}
                    </Tile>
                  ))}
                </div>
              </div>
              <div>
                <label className="field-label">{t("Mileage / month")}</label>
                <div className="flex gap-2">
                  {[{ m: 3000 }, { m: 4000, up: 12 }, { m: 5000, up: 22 }].map(({ m, up }) => (
                    <Tile key={m} active={mileage === m} onClick={() => setMileage(m)} testId={`mileage-${m}`}>
                      {t("{{n}} km", { n: m.toLocaleString() })}
                      {up && <span className="absolute -top-2 -right-2 bg-[#144733] text-white text-[9px] rounded-full px-1.5 py-0.5 normal-case">+{up}%</span>}
                    </Tile>
                  ))}
                </div>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-[#0F1E17] cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-testid="monthly-age-checkbox" />
              <span>{t("I am above the age of 21 and I am eligible to drive in the UAE")}</span>
            </label>
          </div>

          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-6" data-testid="monthly-error">{error}</div>
          )}
          {!data && !error && (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>
          )}
          {data && data.vehicles.length === 0 && (
            <div className="bg-white border border-[#e2ebe4] rounded-lg px-6 py-12 text-center text-sm text-[#57685e]" data-testid="monthly-empty-state">
              {t("Nothing matched this exact setup just yet. Try a shorter term, different mileage or another start date and we'll show you what's free.")}
            </div>
          )}
          {data && data.vehicles.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="monthly-grid">
              {[...data.vehicles].sort((a, b) => a.pricing.monthly_with_vat - b.pricing.monthly_with_vat).map((v) => (
                <VehicleCard key={v.id} vehicle={v} mode="monthly" onSelect={onSelect} onDetails={setDetail} />
              ))}
            </div>
          )}
        </div>

        {/* Sticky included/excluded panel */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-4">
            <div className="bg-white rounded-lg border border-[#e2ebe4] overflow-hidden" data-testid="included-panel">
              <div className="bg-[#144733] text-white px-4 py-3 heading-upper text-sm tracking-widest">{t("What's included")}</div>
              <ul className="p-4 space-y-2">
                {INCLUDED.map((i) => <li key={i} className="flex gap-2 text-xs text-[#0F1E17]"><Check className="w-3.5 h-3.5 text-[#009900] shrink-0 mt-0.5" />{t(i)}</li>)}
              </ul>
              <div className="px-4 pb-2 heading-upper text-[10px] tracking-widest text-[#57685e]">{t("Not included")}</div>
              <ul className="px-4 pb-4 space-y-2">
                {EXCLUDED.map((i) => <li key={i} className="flex gap-2 text-xs text-[#57685e]"><XIcon className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />{t(i)}</li>)}
              </ul>
            </div>
            <div className="bg-[#f4f7f4] border border-[#e2ebe4] rounded-lg p-4 text-xs text-[#144733]" data-testid="early-termination-box">
              <strong>{t("Ending early?")}</strong>{t(" In plain words: finish any started rental month, give us 7 days notice, and we recalculate your rate at the term you actually kept, with no hidden penalties.")}
            </div>
          </div>
        </aside>
      </main>

      <AgeGateModal open={!!pendingVehicle} onClose={() => setPendingVehicle(null)}
        checked={ageChecked} setChecked={setAgeChecked}
        onConfirm={() => { setAgeOk(true); lockIn(pendingVehicle); }} />
      <VehicleDetailModal vehicle={detail} gallery={detail ? galleries[detail.id] : null}
        onClose={() => setDetail(null)} onSelect={(v) => { setDetail(null); onSelect(v); }} />
      <Footer />
    </div>
  );
}
